import type {
    AxiosError,
    AxiosInstance,
    InternalAxiosRequestConfig,
} from 'axios';
import axios from 'axios';
import type { AuthSession } from '@/api/models';
import { PATH } from '@/router/PATH';
import {
    clearAuthSession,
    getAccessToken,
    getRefreshToken,
    setSession,
} from '@/utils/authSession';

/** Requests we must never refresh/retry: a 401 from them is the real answer. */
const isAuthEndpoint = (url: string | undefined): boolean =>
    typeof url === 'string' && url.includes('/auth/');

/** Marker on a request config so each request is only ever retried once. */
type RetriableConfig = InternalAxiosRequestConfig & { _authRetried?: boolean };

/**
 * Single-flight refresh: concurrent 401s share one in-flight refresh instead
 * of each firing their own (which would burn through the rotating refresh token
 * and revoke each other). Module-level so it coordinates across every axios
 * instance the factory hands out.
 */
let refreshInFlight: Promise<string | null> | null = null;

/**
 * Perform the token refresh against `<apiBaseUrl>/auth/refresh` using a bare
 * axios call (no interceptors) so a 401 here can't recurse back into this
 * handler. Persists the rotated pair on success and returns the new access
 * token, or null when there's nothing to refresh with / the refresh failed.
 */
const performRefresh = async (apiBaseUrl: string): Promise<string | null> => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
        return null;
    }
    try {
        const response = await axios.post<AuthSession>(
            `${apiBaseUrl}/auth/refresh`,
            { refreshToken },
        );
        const session = response.data;
        if (!session?.accessToken || !session?.refreshToken) {
            return null;
        }
        setSession(session);
        return session.accessToken;
    } catch {
        return null;
    }
};

const refreshAccessToken = (apiBaseUrl: string): Promise<string | null> => {
    if (!refreshInFlight) {
        refreshInFlight = performRefresh(apiBaseUrl).finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
};

/** Clear the dead session and bounce to login (unless we're already there). */
const handleAuthFailure = (): void => {
    clearAuthSession();
    if (
        typeof window !== 'undefined' &&
        window.location.pathname !== PATH.LOGIN
    ) {
        window.location.assign(PATH.LOGIN);
    }
};

/**
 * Attach the bearer + refresh-on-401 behaviour to a client instance. Called by
 * `getAxiosClient` for every instance it produces, so all resource services
 * inherit it without attaching anything per call.
 *
 * `apiBaseUrl` is the already-resolved `<base>/api/v1` URL — used to build the
 * bare refresh request.
 */
export const attachAuthInterceptors = (
    instance: AxiosInstance,
    apiBaseUrl: string,
): void => {
    instance.interceptors.request.use((config) => {
        const token = getAccessToken();
        if (token) {
            config.headers.set('Authorization', `Bearer ${token}`);
        }
        return config;
    });

    instance.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
            const original = error.config as RetriableConfig | undefined;
            const status = error.response?.status;

            if (
                status !== 401 ||
                !original ||
                original._authRetried ||
                isAuthEndpoint(original.url)
            ) {
                return Promise.reject(error);
            }

            original._authRetried = true;

            const newToken = await refreshAccessToken(apiBaseUrl);
            if (!newToken) {
                handleAuthFailure();
                return Promise.reject(error);
            }

            original.headers.set('Authorization', `Bearer ${newToken}`);
            return instance(original);
        },
    );
};

/**
 * Test-only: reset the module-level single-flight latch between cases so one
 * test's in-flight refresh can't leak into the next.
 */
export const resetAuthInterceptorState = (): void => {
    refreshInFlight = null;
};
